import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  MobileCategoryButton,
  MobileSubcategoryButton
} from './styles';
import { getSafeId } from '../../utils/helper';
import { Box } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { HeaderSubmenuCategoryCtaClick, sendGA4Data } from '../../analytics/events';
import { events } from '../../constants';
import { useSelector } from 'react-redux';
import { getMarketplaceListingUrl, getCategoriesAndFacetType, getTranslatedCategories } from '../../utils/helper';
import { useCategoriesData } from './categories';
import { useTranslatedObject } from '../../utils/translation';
import { buildSearchUrl } from '@headless-shell/shared-components';

const MobileExpandedCategories = memo(({
  expandedState,
  onCategoryToggle,
  keyPrefix,
  navigate,
  locale,
  authenticated,
  handleDrawerClose,
  type
}) => {
  const activeTab = useSelector(state => state.app.activeTab);
  const header = useTranslatedObject('header');
  const page = useTranslatedObject('page');
  const solutions_facetType = useTranslatedObject('solutions_facetType');
  const industry_facetType = useTranslatedObject('industry_facetType');
  const solutionCategories = useTranslatedObject('solution_categories');
  const industryCategories = useTranslatedObject('industry_categories');
  const facetName = type === 'solutions' ? 'Solutions' : 'Industry';
  const { categoriesData, isLoading, isError } = useCategoriesData(facetName);

  const { facetType, pageKey } = getCategoriesAndFacetType(type, solutions_facetType, industry_facetType);
  const categories = getTranslatedCategories(categoriesData, type, solutionCategories, industryCategories);

  const handleCategoryCTAClick = (category, ctaName) => {
    const hasAttribute = !!category.attributeId;
    const isViewMoreApps = ctaName === "View more apps";
    const isHRIS = category.label === 'HRIS';
    let pageName = 'listing';
    if (!isViewMoreApps && (hasAttribute || category?.overviewLabel)) {
          pageName = page?.[pageKey] || 'listing';
    }
    const url = getMarketplaceListingUrl({
      locale,
      facetType: isViewMoreApps ? facetType : '',
      solutionName: isViewMoreApps ? category.key : '',
      attributeId: isViewMoreApps ? (isHRIS ? category.attributeId : '') : category.attributeId,
      page: pageName,
      apicLabel: (!isViewMoreApps && category?.overviewLabel) || '',
    });
    const [basePath = '/listing', hashFragment = ''] = url.split('#');

    const targetUrl = pageName === 'listing'
      ? buildSearchUrl({
          searchValue: '',
          hashParam: hashFragment ? `#${hashFragment}` : '',
          isAuthenticated: authenticated,
          basePath: basePath.startsWith('/') ? basePath : `/${basePath}`
        })
      : url;
    sendGA4Data(HeaderSubmenuCategoryCtaClick, {
      customEventName: `${events.EVENT_NAMES.HEADER_SUBMENU}_${activeTab}`,
      eventCategory: category.label,
      eventLabel: ctaName
    });
    handleDrawerClose();
    navigate(targetUrl);
  };

  if (isError) {
    console.error('Product tags API failed - Solutions dropdown disabled');
    return null;
  }

  return (
    <Box id={getSafeId('mobile-expanded', keyPrefix)}>
      {categories.map((category) => {
        const isExpanded = expandedState[category.label];
        const categoryId = getSafeId(`mobile-category-${keyPrefix}`, category.label);
        const subcategoryId = getSafeId(`mobile-subcategory-${keyPrefix}`, category.label);

        return (
          <React.Fragment key={getSafeId(keyPrefix, category.label)}>
            <MobileCategoryButton
              id={categoryId}
              fullWidth
              onClick={() => onCategoryToggle(category.label)}
              endIcon={isExpanded ? <RemoveIcon /> : <AddIcon />}
            >
              {category.label}
            </MobileCategoryButton>

            {isExpanded && (
              <Box id={subcategoryId}>
                {!category.hideOverview && <MobileSubcategoryButton
                  fullWidth
                  onClick={() => handleCategoryCTAClick(category, "Overview")}
                >
                  {header.overview}
                </MobileSubcategoryButton>}
                <MobileSubcategoryButton
                  fullWidth
                  onClick={() => {
                    handleCategoryCTAClick(category, "View more apps");
                    handleDrawerClose();
                  }}
                >
                  {header.view_more_apps}
                </MobileSubcategoryButton>
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
});
MobileExpandedCategories.displayName = 'MobileExpandedCategories';

MobileExpandedCategories.propTypes = {
  expandedState: PropTypes.object.isRequired,
  onCategoryToggle: PropTypes.func.isRequired,
  keyPrefix: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['solutions', 'industries']).isRequired,
  navigate: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  authenticated: PropTypes.bool,
};

export default MobileExpandedCategories;